import { Spinner } from '@common/components';
import { BlockSelectField } from '@common/components/FormElements';
import { AvailabilityForm as AvailabilityFormComponent } from '@common/features/DynamicPackagesConfigurationWizardNew/components/AvailabilityForm/AvailabilityForm';
import { useGetSpaPackageConfigGroups } from '@common/features/spa';
import { useTranslations } from '@lang';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { ConfigurationGroupType } from 'shared-types/SpaPackage';
import { ExperienceType, PackagesFormValues, SelectedExperienceType } from '../../../../types';
import {
  DropdownGridWrapper,
  SpaAvailabilityWrapper,
  DropdownWrapper,
  SpaLabel,
} from '../MultiDayExperiencesStep.styles';
import { usePackageConfiguration } from '../../../../contexts';
import { getSelectedOptionAndExperienceId } from '@common/features/DynamicPackagesConfigurationWizardNew/helpers/getSelectedOptionAndExperienceId';
import { getEffectiveExperienceDate } from '@common/features/DynamicPackagesConfigurationWizardNew/helpers/getEffectiveExperienceDate';
import { formatOptionsForSelect } from '@common/features/DynamicPackagesConfigurationWizardNew/helpers/formatOptionsForSelect';
import { getSelectableOptions } from '@common/features/DynamicPackagesConfigurationWizardNew/helpers/getSelectableOptions';

type Props = {
  experiences: { optionGroupId: number; options: SelectedExperienceType[] };
  configurationGroups: ConfigurationGroupType[];
  experienceIndex: number;
  dayNumberRange: number[];
  guestIndex?: number;
  numberOfGuests?: number;
  isSameTimeForAllGuests?: boolean;
  onToggleSameTime?: () => void;
};

export const MultipleOptionsExperienceTimeslotSelection: React.FC<Props> = ({
  experiences,
  configurationGroups,
  experienceIndex,
  dayNumberRange,
  guestIndex = 0,
  numberOfGuests = 1,
  isSameTimeForAllGuests = false,
}) => {
  const { property, spaPackageId, spaOpenTime, spaCloseTime, journeyParams } =
    usePackageConfiguration();
  const { t } = useTranslations();
  const { watch, setValue } = useFormContext<PackagesFormValues>();
  const delayAfter = watch(`selectedExperiences.${experienceIndex}.delayAfter`);
  const guests = watch('guests') || [];
  const guest = guests[guestIndex];

  const selectedDayNumber = watch(`guests.${guestIndex}.experiences.${experienceIndex}.dayNumber`);

  const { formattedDate: relevantDate } = getEffectiveExperienceDate(
    journeyParams.fromDate,
    selectedDayNumber,
    dayNumberRange
  );

  const { data: configGroups, isFetching } = useGetSpaPackageConfigGroups(
    configurationGroups,
    property.id,
    Number(spaPackageId),
    relevantDate,
    isSameTimeForAllGuests ? numberOfGuests : 1,
    spaOpenTime,
    spaCloseTime
  );

  const selectableOptions = getSelectableOptions(configGroups, experiences);

  const minimumTimeslotQuantity = isSameTimeForAllGuests ? numberOfGuests : 1;
  const selectableOptionsFormatted = formatOptionsForSelect(selectableOptions);

  const experience = watch(`guests.${guestIndex}.experiences.${experienceIndex}` as any);
  const { selectedOption, selectedExperienceId, selectedExperienceValue } =
    getSelectedOptionAndExperienceId(
      selectableOptionsFormatted,
      experience?.optionId,
      selectableOptions,
      experiences
    );

  return selectableOptions ? (
    <>
      <Spinner isActive={isFetching} isBackgroundDisabled={true} />

      <SpaAvailabilityWrapper
        data-testid={`multiple-options-selection-guest-${guestIndex}-group-${experiences.optionGroupId}`}
      >
        {!isSameTimeForAllGuests && guest && (
          <SpaLabel>
            {guest.guestDetails?.title} {guest.guestDetails?.firstName}{' '}
            {guest.guestDetails?.lastName}
          </SpaLabel>
        )}
        <SpaLabel required>{t('tables.menuSelect')}</SpaLabel>
        <DropdownGridWrapper>
          <DropdownWrapper>
            <BlockSelectField
              options={selectableOptionsFormatted}
              onChange={(val) => {
                setValue(`guests.${guestIndex}.experiences.${experienceIndex}`, {
                  ...experience,
                  optionId: Number(val.value),
                  startTime: undefined,
                  endTime: undefined,
                  visitDate: undefined,
                  dayNumber: undefined,
                } as any);
              }}
              placeholder={t('spa.toggle.select')}
              value={selectedExperienceValue}
              useNativeDropdownOnMobile={false}
              isSearchable={false}
              menuPlacement={'auto'}
              noOptionsMessage={() => t('spa.noOptionsAvailable')}
            />
          </DropdownWrapper>
        </DropdownGridWrapper>
        {selectedOption && selectedExperienceId && (
          <DropdownGridWrapper>
            <AvailabilityFormComponent
              id={Number(selectedExperienceId)}
              entityType={selectedOption.type as ExperienceType}
              timeslots={selectedOption.timeslots || []}
              namePrefix={`guests.${guestIndex}.experiences.${experienceIndex}`}
              guestIndex={guestIndex}
              delayAfter={delayAfter}
              dayNumber={undefined}
              dayNumberRange={dayNumberRange}
              experienceIndex={experienceIndex}
              minimumTimeslotQuantity={minimumTimeslotQuantity}
              showDaySelector
            />
          </DropdownGridWrapper>
        )}
      </SpaAvailabilityWrapper>
    </>
  ) : null;
};
